import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import PostRequest from "../../../services/PostRequest";
import { showToaster } from "../../../common/common";
import { useMemo, useState } from "react";
import {
  State,
  City,
  getCountryOptions,
  getPostcodeHint,
  countryHasNoPostcode,
  countryName,
} from "../../../common/locationData";

export const SignUp = () => {
  const navigate = useNavigate();
  const countryOptions = useMemo(() => getCountryOptions(), []);
  const [currentStep, setCurrentStep] = useState(1);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState("");
  const [countryState, setCountryState] = useState([]);
  const [stateCity, setStateCity] = useState([]);
  const [activeCountry, setActiveCountry] = useState("");
  const [activeState, setActiveState] = useState("");
  const [activeCity, setActiveCity] = useState("");

  const handleSignUp = async (data) => {
    try {
      const option = {
        showLoader: "register-id",
        auth: false,
      };

      // Submit human-readable names (activeCountry/activeState hold the ISO
      // codes used for the country-state-city lookups).
      data.country = countryName(activeCountry);
      data.state =
        State.getStateByCodeAndCountry(activeState, activeCountry)?.name ||
        activeState;
      data.city = activeCity;

      const response = await PostRequest(
        "/users/register/otp/sent",
        data,
        option
      );

      navigate("/sign-up/verify", { state: data });
    } catch (error) {
      console.log("error", error.message);
      showToaster(error.message, "error");
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.name) errors.name = "Name is Required";

    if (!values.email) {
      errors.email = "Email Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Invalid email address";
    }

    if (currentStep === 2) {
      if (!activeCountry) errors.country = "Country is Required";
      if (!values.postal_code && !countryHasNoPostcode(activeCountry))
        errors.postal_code = "Postal code is Required";
      if (!values.termsAccepted) errors.termsAccepted = "You must accept the terms";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      business_name: "",
      address_1: "",
      address_2: "",
      postal_code: "",
      termsAccepted: false,
    },
    validate,
    onSubmit: handleSignUp,
  });

  const handleOnChangeCountry = (e) => {
    const countryCode = e.target.value; // ISO-2
    setActiveCountry(countryCode);
    setCountryState(State.getStatesOfCountry(countryCode) || []);
    setActiveState("");
    setStateCity([]);
    setActiveCity("");
  };

  const handleOnChangeState = (e) => {
    const stateCode = e.target.value; // ISO state code
    setActiveState(stateCode);
    setStateCity(City.getCitiesOfState(activeCountry, stateCode) || []);
    setActiveCity("");
  };

  const handleCityChange = (e) => {
    const selectedOption = e.target.value;
    setActiveCity(selectedOption);
  };

  const handleStepOne = async () => {
    // Validate step 1 fields
    if (!formik.values.name) {
      formik.setFieldTouched("name", true);
      showToaster("Please enter your name", "error");
      return;
    }
    if (!formik.values.email) {
      formik.setFieldTouched("email", true);
      showToaster("Please enter your email", "error");
      return;
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formik.values.email)) {
      formik.setFieldTouched("email", true);
      showToaster("Please enter a valid email", "error");
      return;
    }

    // Don't make the user fill out step 2 only to be told the email is taken —
    // check availability now. (send_registration_otp re-checks authoritatively.)
    setEmailExistsError("");
    setIsCheckingEmail(true);
    try {
      const res = await PostRequest(
        "/users/email/check",
        { email: formik.values.email },
        { auth: false }
      );
      if (res && res.available === false) {
        // Dedicated state, NOT formik.setFieldError: formik re-validates on
        // setFieldTouched/setFieldValue and would immediately wipe a manually
        // set error (the email passes format validation), so it never showed.
        setEmailExistsError(
          "An account with this email already exists. Try signing in instead."
        );
        showToaster("An account with this email already exists.", "error");
        return;
      }
    } catch (e) {
      // Network/endpoint failure shouldn't block signup — step 2 re-checks.
      console.log("email availability check failed:", e.message);
    } finally {
      setIsCheckingEmail(false);
    }

    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  return (
    <div className="auth">
      <div className="auth__main">
        <div className="auth__card">
          <img
            className="auth__logo auth__logo--color"
            src="/assets/brand/multicartapi-logo.png"
            alt="MultiCartAPI"
          />
          <img
            className="auth__logo auth__logo--white"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h1>Create your account</h1>
          <p className="auth__sub">
            Start testing the Amazon &amp; Officeworks APIs in minutes.
          </p>

          <div className="mca-stepper" style={{ marginBottom: "26px" }}>
            <div
              className={`mca-step ${currentStep > 1 ? "is-done" : "is-active"}`}
              id="step1"
            >
              <div className="mca-step__dot">1</div>
              <div className="mca-step__label">Account</div>
            </div>
            <div
              className={`mca-step__line ${currentStep > 1 ? "is-done" : ""}`}
              id="stepLine"
            ></div>
            <div
              className={`mca-step ${currentStep === 2 ? "is-active" : ""}`}
              id="step2"
            >
              <div className="mca-step__dot">2</div>
              <div className="mca-step__label">Business &amp; address</div>
            </div>
          </div>

          <form className="auth__form" onSubmit={formik.handleSubmit}>
            {/* Step 1: Account */}
            {currentStep === 1 && (
              <div data-pane="1">
                <div className="mca-field" style={{ marginBottom: "18px" }}>
                  <label className="mca-label" htmlFor="su-name">
                    Full name <span className="req">*</span>
                  </label>
                  <input
                    className="mca-input"
                    id="su-name"
                    name="name"
                    placeholder="Alex Lawson"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.name && formik.errors.name && (
                    <span className="mca-error">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {formik.errors.name}
                    </span>
                  )}
                </div>

                <div className="mca-field">
                  <label className="mca-label" htmlFor="su-email">
                    Email <span className="req">*</span>
                  </label>
                  <input
                    className="mca-input"
                    id="su-email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={formik.values.email}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (emailExistsError) setEmailExistsError("");
                    }}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.email && formik.errors.email && (
                    <span className="mca-error">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {formik.errors.email}
                    </span>
                  )}
                  {emailExistsError && (
                    <span className="mca-error" style={{ color: "var(--mca-danger)" }}>
                      {emailExistsError}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="mca-btn mca-btn--primary"
                  style={{ width: "100%", marginTop: "22px" }}
                  onClick={handleStepOne}
                  disabled={isCheckingEmail}
                >
                  {isCheckingEmail ? "Checking…" : "Continue"}
                </button>
              </div>
            )}

            {/* Step 2: Business & Address */}
            {currentStep === 2 && (
              <div data-pane="2">
                <div className="mca-field" style={{ marginBottom: "16px" }}>
                  <label className="mca-label" htmlFor="su-biz">
                    Business name
                    <span className="mca-help" style={{ fontWeight: "400" }}>
                      (optional)
                    </span>
                  </label>
                  <input
                    className="mca-input"
                    id="su-biz"
                    name="business_name"
                    placeholder="Brightstore Pty Ltd"
                    value={formik.values.business_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="mca-field" style={{ marginBottom: "16px" }}>
                  <label className="mca-label" htmlFor="su-a1">
                    Address line 1
                    <span className="mca-help" style={{ fontWeight: "400" }}>
                      (optional)
                    </span>
                  </label>
                  <input
                    className="mca-input"
                    id="su-a1"
                    name="address_1"
                    placeholder="Level 4, 120 Collins Street"
                    value={formik.values.address_1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="mca-field" style={{ marginBottom: "16px" }}>
                  <label className="mca-label" htmlFor="su-a2">
                    Address line 2
                    <span className="mca-help" style={{ fontWeight: "400" }}>
                      (optional)
                    </span>
                  </label>
                  <input
                    className="mca-input"
                    id="su-a2"
                    name="address_2"
                    placeholder="Suite 1000"
                    value={formik.values.address_2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "16px" }}>
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="su-country">
                      Country <span className="req">*</span>
                    </label>
                    <select
                      className="mca-select"
                      id="su-country"
                      name="country"
                      value={activeCountry}
                      onChange={handleOnChangeCountry}
                      required
                    >
                      <option value="">Select…</option>
                      {countryOptions.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.country && formik.errors.country && (
                      <span className="mca-error">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {formik.errors.country}
                      </span>
                    )}
                  </div>

                  <div className="mca-field">
                    <label className="mca-label" htmlFor="su-state">
                      State
                    </label>
                    <select
                      className="mca-select"
                      id="su-state"
                      name="state"
                      value={activeState}
                      onChange={handleOnChangeState}
                      disabled={!activeCountry}
                    >
                      <option value="">
                        {activeCountry ? "Select state…" : "Select country first"}
                      </option>
                      {countryState.map((state, index) => (
                        <option key={index} value={state.isoCode || state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "16px" }}>
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="su-city">
                      City
                    </label>
                    <select
                      className="mca-select"
                      id="su-city"
                      name="city"
                      value={activeCity}
                      onChange={handleCityChange}
                      disabled={!activeState}
                    >
                      <option value="">
                        {activeState ? "Select city…" : "Select state first"}
                      </option>
                      {stateCity.map((city, index) => (
                        <option key={index} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mca-field">
                    <label className="mca-label" htmlFor="su-zip">
                      Postal code <span className="req">*</span>
                    </label>
                    <input
                      className="mca-input"
                      id="su-zip"
                      type="text"
                      inputMode="text"
                      name="postal_code"
                      placeholder={
                        activeCountry
                          ? getPostcodeHint(activeCountry).example || "Postal / ZIP code"
                          : "Select country first"
                      }
                      value={formik.values.postal_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={!activeCountry}
                      required={!countryHasNoPostcode(activeCountry)}
                    />
                    {activeCountry && getPostcodeHint(activeCountry).format && (
                      <span className="mca-help">
                        Format: {getPostcodeHint(activeCountry).format}
                      </span>
                    )}
                    {formik.touched.postal_code && formik.errors.postal_code && (
                      <span className="mca-error">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {formik.errors.postal_code}
                      </span>
                    )}
                  </div>
                </div>

                <label className="mca-check" style={{ marginTop: "4px" }}>
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formik.values.termsAccepted}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  <span className="mca-check__box">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span>
                    I agree to the{" "}
                    <a
                      href="https://multicartapi.com/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </a>
                    {" "}and{" "}
                    <a
                      href="https://multicartapi.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>

                <div className="auth__row" style={{ marginTop: "22px", flexWrap: "nowrap", gap: "10px" }}>
                  <button
                    type="button"
                    className="mca-btn mca-btn--outline"
                    style={{ flex: "none" }}
                    onClick={handleBack}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m15 6-6 6 6 6" />
                    </svg>
                    Back
                  </button>
                  <button
                    type="submit"
                    className="mca-btn mca-btn--primary"
                    id="su-btn"
                    style={{ flex: "1" }}
                    disabled={formik.isSubmitting}
                  >
                    <span
                      className="mca-btn__spin"
                      hidden={!formik.isSubmitting}
                    >
                      <span className="mca-spinner"></span>
                    </span>
                    <span className="mca-btn__label">
                      {formik.isSubmitting ? "Creating…" : "Create account"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </form>

          <div className="auth__foot">
            Already have an account?{" "}
            <a href="/sign-in">Sign in</a>
          </div>
        </div>
      </div>

      <div data-toast-host></div>

      {/* Brand Panel */}
      <div className="auth__brand">
        <div>
          <img
            className="auth__brand-logo"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h2>One key. Every cart.</h2>
          <p>
            Create a free account and start pulling live Amazon &amp; Officeworks data on your schedule.
          </p>
        </div>
        <ul className="auth__feat">
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            Free Tier - 500 Scrapes
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            No card required to start
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            Live in under 5 minutes
          </li>
        </ul>
        <div className="auth__brand-foot">
          © 2024 MultiCartAPI. All rights reserved.
        </div>
      </div>
    </div>
  );
};
